import {
  type approximateCountResponseSchema,
  type authorizationRequestResponseSchema,
  type authorizationResponseSchema,
  type createFieldMappingRequestSchema,
  type createIntegrationInstanceRequestSchema,
  customFieldFilterSchema,
  type fieldMappingSchema,
  type fieldMappingSummarySchema,
  type fieldMappingUsageSchema,
  type fieldValueMappingRequestSchema,
  type fieldValueMappingSchema,
  type instanceLevelMappingSchema,
  type instanceTypeMappingSchema,
  integrationCustomFieldMappingSchema,
  integrationFiltersSchema,
  type integrationInstanceSchema,
  integrationSchema,
  integrationSyncErrorResponseSchema,
  itemSyncStatusResponseSchema,
  type jiraAutocompleteSuggestionResponseSchema,
  type jiraAutocompleteSuggestionSchema,
  type jiraCustomFieldMetadataSchema,
  type jiraFieldModelSchema,
  type jiraFieldValueSchema,
  type jiraInstanceSchema,
  type jiraIssueTypeSchema,
  type jiraProjectComponentSchema,
  type jiraProjectModelSchema,
  type jiraProjectTypesAndStatusesModelSchema,
  type jiraStatusModelSchema,
  type jiraVersionModelSchema,
  type loopFieldValueSchema,
  type mappingFieldsSchema,
  type oAuthCredentialStatusResponseSchema,
  oAuthCredentialStatusSchema,
  type pendingItemsCountResponseSchema,
  type saveFieldValueMappingsRequestSchema,
  type updateFieldMappingRequestSchema,
  type userSuggestionSchema,
  type userSuggestionsResponseSchema,
  type validateCustomFiltersResponseSchema,
  type validateJqlRequestSchema,
  type validateJqlResponseSchema,
} from '@tempo-io/work-integration-dto';
import type { z } from 'zod';
import i18n from '@/common/translations/i18n';

export type JiraInstance = z.infer<typeof jiraInstanceSchema>;

export type JiraProject = z.infer<typeof jiraProjectModelSchema>;

export type JiraIssueType = z.infer<typeof jiraIssueTypeSchema>;

export type JiraStatus = z.infer<typeof jiraStatusModelSchema>;

export type JiraTeamField = z.infer<typeof jiraFieldModelSchema>;

export type JiraEstimateField = z.infer<typeof jiraFieldModelSchema>;

export type JiraDateField = z.infer<typeof jiraFieldModelSchema>;

export type JiraFields = z.infer<typeof mappingFieldsSchema>;

export type JiraIssueTypesAndStatusesResponse = z.infer<
  typeof jiraProjectTypesAndStatusesModelSchema
>;

export type JiraIssueTypesWithHierarchyResponse = z.infer<typeof jiraIssueTypeSchema>;

export type JiraProjectItemCountResponse = z.infer<typeof approximateCountResponseSchema>;

export type ValidateJqlRequest = z.infer<typeof validateJqlRequestSchema>;

export type ValidateJqlResponse = z.infer<typeof validateJqlResponseSchema>;

export type JiraOauthRequestResponse = z.infer<typeof authorizationRequestResponseSchema>;

export type JiraOauthResponseResponse = z.infer<typeof authorizationResponseSchema>;

export interface WorkIntegrationSyncRequest {
  syncType: SyncType;
  sourceItemIds?: string[];
  itemIds?: readonly number[];
}

export const SyncType = {
  PULL: 'PULL',
  PUSH: 'PUSH',
} as const;

export type SyncType = (typeof SyncType)[keyof typeof SyncType];

export const WorkIntegration_Keys = integrationSchema.shape.sourceSystemType.enum;

export type WorkIntegration_Keys = (typeof WorkIntegration_Keys)[keyof typeof WorkIntegration_Keys];

export const ExternalWorkIntegrationStatusValues = integrationSchema.shape.status.enum;

export type ExternalWorkIntegrationStatusValues =
  (typeof ExternalWorkIntegrationStatusValues)[keyof typeof ExternalWorkIntegrationStatusValues];

export const SynchronizationStatusValues = integrationSchema.shape.synchronizationStatus.enum;

export type SynchronizationStatusValues =
  (typeof SynchronizationStatusValues)[keyof typeof SynchronizationStatusValues];

export const FILTER_MODE_VALUES = integrationFiltersSchema.shape.filterMode.options;

export type WorkIntegrationFilters = Partial<
  Omit<z.infer<typeof integrationFiltersSchema>, 'customFieldFilters'>
> & {
  customFieldFilters?: JiraCustomFieldFilter[];
};

export const SyncDirection = integrationCustomFieldMappingSchema.shape.syncDirection.enum;

export type SyncDirection = (typeof SyncDirection)[keyof typeof SyncDirection];

export type IntegrationCustomFieldMapping = z.infer<typeof integrationCustomFieldMappingSchema>;

export type WorkIntegrationResponse = z.infer<typeof integrationSchema>;

export type JiraItemLevelMapping = z.infer<typeof instanceLevelMappingSchema> & {
  instanceId: string;
};

export interface ItemLevelMapping {
  itemLevel: number;
  sourceLevel: number;
}

export type JiraItemTypeMapping = z.infer<typeof instanceTypeMappingSchema> & {
  instanceId: string;
};

export interface IssueTypeMapping {
  itemTypeId: number;
  sourceTypeId: string;
}

export const WorkIntegrationSyncErrorType = integrationSyncErrorResponseSchema.shape.type.enum;

export type WorkIntegrationSyncErrorType =
  (typeof WorkIntegrationSyncErrorType)[keyof typeof WorkIntegrationSyncErrorType];

export type WorkIntegrationSyncErrorResponse = z.infer<typeof integrationSyncErrorResponseSchema>;

export const JIRA_FIELD_TYPE_VALUES = customFieldFilterSchema.shape.fieldType.options;
export type JiraFieldType = (typeof JIRA_FIELD_TYPE_VALUES)[number];

export const JIRA_JQL_OPERATOR_VALUES = customFieldFilterSchema.shape.operator.options;
export type JiraJqlOperator = (typeof JIRA_JQL_OPERATOR_VALUES)[number];

export const OPERATOR_LABEL_KEYS = {
  EQUALS: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.equals),
  NOT_EQUALS: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.doesNotEqual),
  CONTAINS: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.contains),
  NOT_CONTAINS: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.doesNotContain),
  IN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.isOneOf),
  NOT_IN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.isNotOneOf),
  IS_EMPTY: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.isEmpty),
  IS_NOT_EMPTY: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.isNotEmpty),
  GREATER_THAN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.greaterThan),
  GREATER_THAN_OR_EQUALS: i18n.t(
    ($) => $.common.settings['work-integrations'].jira.operators.greaterThanOrEquals,
  ),
  LESS_THAN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.lessThan),
  LESS_THAN_OR_EQUALS: i18n.t(
    ($) => $.common.settings['work-integrations'].jira.operators.lessThanOrEquals,
  ),
  BETWEEN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.between),
  NOT_BETWEEN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.notBetween),
} as const;

const STANDARD_VALUE_OPERATORS = [
  'EQUALS',
  'NOT_EQUALS',
  'IN',
  'NOT_IN',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
] as const satisfies readonly JiraJqlOperator[];

const MULTI_VALUE_OPERATORS = [
  'IN',
  'NOT_IN',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
] as const satisfies readonly JiraJqlOperator[];

const TEXT_OPERATORS = [
  'CONTAINS',
  'NOT_CONTAINS',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
] as const satisfies readonly JiraJqlOperator[];

export const OPERATORS_BY_FIELD_TYPE: Record<JiraFieldType, readonly JiraJqlOperator[]> = {
  SELECT: STANDARD_VALUE_OPERATORS,
  MULTI_SELECT: MULTI_VALUE_OPERATORS,
  DATE: [
    'EQUALS',
    'NOT_EQUALS',
    'GREATER_THAN',
    'GREATER_THAN_OR_EQUALS',
    'LESS_THAN',
    'LESS_THAN_OR_EQUALS',
    'BETWEEN',
    'NOT_BETWEEN',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  DATETIME: [
    'EQUALS',
    'NOT_EQUALS',
    'GREATER_THAN',
    'GREATER_THAN_OR_EQUALS',
    'LESS_THAN',
    'LESS_THAN_OR_EQUALS',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  TEXT: TEXT_OPERATORS,
  MULTILINE_TEXT: TEXT_OPERATORS,
  NUMERIC: [
    'EQUALS',
    'NOT_EQUALS',
    'GREATER_THAN',
    'GREATER_THAN_OR_EQUALS',
    'LESS_THAN',
    'LESS_THAN_OR_EQUALS',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  USER: STANDARD_VALUE_OPERATORS,
  MULTI_USER: MULTI_VALUE_OPERATORS,
  TEAM: STANDARD_VALUE_OPERATORS,
  VERSION: STANDARD_VALUE_OPERATORS,
  COMPONENT: STANDARD_VALUE_OPERATORS,
  LABELS: STANDARD_VALUE_OPERATORS,
  CHECKBOX: ['EQUALS', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  MULTI_CHECKBOX: MULTI_VALUE_OPERATORS,
};

export type JiraCustomFieldMetadata = z.infer<typeof jiraCustomFieldMetadataSchema>;

export type JiraFieldSuggestion = z.infer<typeof jiraAutocompleteSuggestionSchema>;

export type JiraFieldSuggestionsResponse = z.infer<typeof jiraAutocompleteSuggestionResponseSchema>;

export interface JiraFieldSuggestionsParams {
  fieldName: string;
  fieldValue?: string;
}

export type UserSuggestion = z.infer<typeof userSuggestionSchema>;

export type UserSuggestionsResponse = z.infer<typeof userSuggestionsResponseSchema>;

export interface UserSuggestionsParams {
  credentialsId: number;
  instanceId: string;
  searchTerm: string;
}

export interface UsersBulkParams {
  credentialsId: number;
  instanceId: string;
  accountIds: string[];
}

export type ProjectVersion = z.infer<typeof jiraVersionModelSchema>;

export type ProjectComponent = z.infer<typeof jiraProjectComponentSchema>;

export interface ProjectVersionsParams {
  instanceId: string;
  projectKey: string;
  includeArchived?: boolean;
}

export interface ProjectComponentsParams {
  instanceId: string;
  projectKey: string;
}

export type JiraCustomFieldFilter = Partial<z.infer<typeof customFieldFilterSchema>> & {
  id?: string;
};

export interface FilterValidationErrors {
  field?: string;
  operator?: string;
  value?: string;
}

export interface CustomFieldFilter {
  fieldId: string;
  fieldType: JiraFieldType;
  operator: JiraJqlOperator;
  value?: string | string[] | number | null;
}

export type ValidateCustomFieldFiltersResponse = z.infer<
  typeof validateCustomFiltersResponseSchema
>;

export type WorkIntegrationInstance = z.infer<typeof integrationInstanceSchema>;

export const OAuthCredentialAuthStatusValues = oAuthCredentialStatusSchema.shape.status.enum;

export type OAuthCredentialStatus = z.infer<typeof oAuthCredentialStatusSchema>;

export type OAuthCredentialStatusResponse = z.infer<typeof oAuthCredentialStatusResponseSchema>;

export type PendingItemsCountResponse = z.infer<typeof pendingItemsCountResponseSchema>;

export type CreateWorkIntegrationInstanceRequest = z.infer<
  typeof createIntegrationInstanceRequestSchema
>;

export const INSTANCE_JIRA_FIELD_TYPE_VALUES = [
  'SELECT',
  'MULTI_SELECT',
  'DATE',
  'TEXT',
  'MULTILINE_TEXT',
  'NUMERIC',
  'CHECKBOX',
  'COMPONENT',
] as const satisfies readonly JiraFieldType[];

export type InstanceJiraFieldType = (typeof INSTANCE_JIRA_FIELD_TYPE_VALUES)[number];

export type InstanceFieldMapping = z.infer<typeof fieldMappingSchema>;

export type CreateInstanceFieldMappingRequest = z.infer<typeof createFieldMappingRequestSchema>;

export type UpdateInstanceFieldMappingRequest = z.infer<typeof updateFieldMappingRequestSchema>;

export type FieldMappingUsage = z.infer<typeof fieldMappingUsageSchema>;

export type FieldValueMapping = z.infer<typeof fieldValueMappingSchema>;

export type LoopFieldValue = z.infer<typeof loopFieldValueSchema>;

export type JiraFieldValue = z.infer<typeof jiraFieldValueSchema>;

export type SaveFieldValueMappingItem = z.infer<typeof fieldValueMappingRequestSchema>;

export type SaveFieldValueMappingsRequest = z.infer<typeof saveFieldValueMappingsRequestSchema>;

export type FieldMappingSummary = z.infer<typeof fieldMappingSummarySchema>;

export const ItemSyncStatusView = itemSyncStatusResponseSchema.shape.state.enum;

export type ItemSyncStatusView = (typeof ItemSyncStatusView)[keyof typeof ItemSyncStatusView];

export type ItemSyncStatusResponse = z.infer<typeof itemSyncStatusResponseSchema>;
